var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import produce from 'immer';
import React, { useEffect, useReducer, useRef } from 'react';
import { getPatientDecisionSupportSummary } from '../../Async/DecisionSupportAsync';
import { useError } from '../../Components/ErrorBoundary';
import { DecisionSupportStatus, DecisionSupportStatusBehaviour } from '../../Contracts/MedicationModels';
import { CancelledRequest, useHttpConfig } from '../../Shared/HttpRequestExecutor';
import { useDecisionSupportStatusUpdatedSignalR } from '../../SignalR/DecisionSupportSignalR';
import { StepViewer } from './StepViewer';
export var DecisionSupportStep;
(function (DecisionSupportStep) {
    DecisionSupportStep[DecisionSupportStep["Loading"] = 0] = "Loading";
    DecisionSupportStep[DecisionSupportStep["Waiting"] = 1] = "Waiting";
    DecisionSupportStep[DecisionSupportStep["Timeout"] = 2] = "Timeout";
    DecisionSupportStep[DecisionSupportStep["NotedStep"] = 3] = "NotedStep";
})(DecisionSupportStep || (DecisionSupportStep = {}));
var reducer = produce(function (state, actions) {
    switch (actions.type) {
        case 'SkipDecisionSupportWaiting':
            state.step = DecisionSupportStep.NotedStep;
            state.skipped = true;
            break;
        case 'ProcessStatusResponse':
            if (!actions.skipWaiting && actions.response.status === DecisionSupportStatus.NotChecked) {
                state.step = DecisionSupportStep.Timeout;
            }
            else {
                state.step = DecisionSupportStep.NotedStep;
            }
            state.response = actions.response;
            break;
        case 'WaitForDecisionSupport':
            state.step = DecisionSupportStep.Waiting;
            break;
    }
});
var MIN_FINALISE_DURATION = 1000;
var TimeOutWaitDurationMs = 15000;
export var DecisionSupport = function (props) {
    var httpConfig = useHttpConfig();
    var throwError = useError();
    var loadDecisionSupport = function () { return __awaiter(void 0, void 0, void 0, function () {
        var timeout, query, _a, results, error_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    timeout = new Promise(function (resolve) { return setTimeout(resolve, MIN_FINALISE_DURATION); });
                    query = {
                        patientMedication: props.patientMedication,
                        decisionSupportBehaviour: props.excludeNotedDecisionSupport
                            ? DecisionSupportStatusBehaviour.ExcludeNotedDecisionSupport
                            : DecisionSupportStatusBehaviour.CurrentStatus
                    };
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 5]);
                    return [4 /*yield*/, Promise.all([
                            getPatientDecisionSupportSummary(httpConfig, props.patient, query),
                            timeout
                        ])];
                case 2:
                    _a = __read.apply(void 0, [_b.sent(), 1]), results = _a[0];
                    if (results instanceof CancelledRequest)
                        return [2 /*return*/];
                    // If everything has been noted, just skip straight out
                    if (results.noted && !props.patientMedication) {
                        props.onContinue();
                    }
                    else {
                        dispatch({
                            type: 'ProcessStatusResponse',
                            // This indicates we want to avoid the waiting process if a medication has come in
                            // The only scenario we should get a medication for now is because it's from the medication
                            // form view.
                            skipWaiting: !!props.patientMedication,
                            response: results
                        });
                    }
                    return [3 /*break*/, 5];
                case 3:
                    error_1 = _b.sent();
                    return [4 /*yield*/, timeout];
                case 4:
                    _b.sent();
                    throwError(error_1);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        // Load on first entry
        loadDecisionSupport();
    }, []);
    var _a = __read(useReducer(reducer, {
        step: DecisionSupportStep.Loading,
        skipped: false,
        response: null
    }), 2), state = _a[0], dispatch = _a[1];
    // Dirty hack to stop react/javascript from snapshotting everything
    var staleProofState = useRef(state.step);
    staleProofState.current = state.step;
    useDecisionSupportStatusUpdatedSignalR({
        patient: props.patient,
        onSignalResponse: function (decisionSupportStatusUpdated) {
            if (staleProofState.current === DecisionSupportStep.Waiting) {
                loadDecisionSupport();
            }
        }
    });
    var handleContinue = function () {
        props.onContinue();
    };
    var waitTimeout;
    var startWaitTimout = function () {
        if (waitTimeout) {
            clearTimeout(waitTimeout);
        }
        waitTimeout = setTimeout(function () {
            if (staleProofState.current === DecisionSupportStep.Waiting) {
                loadDecisionSupport();
            }
        }, TimeOutWaitDurationMs);
    };
    useEffect(function () {
        return function () {
            if (waitTimeout) {
                clearTimeout(waitTimeout);
            }
        };
    }, []);
    return (React.createElement("div", null,
        React.createElement(StepViewer, { patient: props.patient, patientMedication: props.patientMedication, state: state, onContinue: handleContinue, onNavigateToMedication: props.onNavigateToMedication, onSkip: function () {
                dispatch({ type: 'SkipDecisionSupportWaiting' });
            }, onWait: function () {
                // Start timer and signal
                startWaitTimout();
                dispatch({ type: 'WaitForDecisionSupport' });
            } })));
};
